/*
 * Settings → Operations → Radiation (fieldiq-api#1334, RAD-1).
 *
 * The tenant's radiation settings — GET / PUT /radiation/settings:
 *   - leak-test interval in months (1 to 60) — sets the next-due day of a
 *     leak test recorded without one, and each test keeps the interval it was
 *     recorded under;
 *   - warning days (1 to 5 whole numbers, 1 to 365) — when a leak test reads
 *     Due soon and when the leak-test-due notice goes out;
 *   - dose investigation level, in the tenant's dose unit, or blank for none;
 *   - out-of-shield alert hours, or blank for none.
 *
 * Shared module, like Equipment: no vertical gate on the tab. Writes are admin
 * and office staff (plus a dispatcher with admin access); everyone else reads
 * with the controls disabled. A tenant with no row reads the API's defaults
 * with `stored: false`, and the page says so. Only changed fields are sent.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useRadSettings, useUpdateRadSettings } from '../../hooks/useRadiation';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { RAD_ALERT } from '../../components/radiation/RadBits';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminRad, radErrorMessage } from '../../lib/radiation';
import type { RadSettings, RadSettingsWrite } from '../../types/radiation';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';

export interface RadSettingsTexts {
  interval: string;
  warnDays: string;
  doseLevel: string;
  shieldHours: string;
}

export const radSettingsTexts = (s: RadSettings): RadSettingsTexts => ({
  interval: s.leakTestIntervalMonths === null ? '' : String(s.leakTestIntervalMonths),
  warnDays: s.warnDays.join(', '),
  doseLevel: s.doseInvestigationLevel === null ? '' : String(s.doseInvestigationLevel),
  shieldHours: s.outOfShieldAlertHours === null ? '' : String(s.outOfShieldAlertHours),
});

/** The PUT body for what changed, or an error sentence. */
export function radSettingsBody(before: RadSettings, t: RadSettingsTexts, doseUnit: string): RadSettingsWrite | string {
  const body: RadSettingsWrite = {};
  const interval = t.interval.trim();
  if (!/^\d+$/.test(interval) || Number(interval) < 1 || Number(interval) > 60) {
    return 'The leak-test interval must be a whole number of months from 1 to 60.';
  }
  if (Number(interval) !== before.leakTestIntervalMonths) body.leak_test_interval_months = Number(interval);

  const parts = t.warnDays.split(',').map((p) => p.trim()).filter((p) => p !== '');
  if (parts.length < 1 || parts.length > 5 || !parts.every((p) => /^\d+$/.test(p) && Number(p) >= 1 && Number(p) <= 365)) {
    return 'Warning days must be 1 to 5 whole numbers from 1 to 365, separated by commas.';
  }
  const days = parts.map(Number);
  const same = days.length === before.warnDays.length && days.every((d, i) => d === before.warnDays[i]);
  if (!same) body.warn_days = days;

  const dose = t.doseLevel.trim();
  let doseValue: number | null = null;
  if (dose !== '') {
    doseValue = Number(dose);
    if (!Number.isFinite(doseValue) || doseValue <= 0) return `The dose investigation level must be a number greater than 0 (${doseUnit || 'tenant unit'}), or blank for none.`;
  }
  if (doseValue !== before.doseInvestigationLevel) body.dose_investigation_level = doseValue;

  const hours = t.shieldHours.trim();
  let hoursValue: number | null = null;
  if (hours !== '') {
    hoursValue = Number(hours);
    if (!Number.isFinite(hoursValue) || hoursValue <= 0 || hoursValue > 9999) return 'The out-of-shield alert must be a number of hours greater than 0, or blank for none.';
  }
  if (hoursValue !== before.outOfShieldAlertHours) body.out_of_shield_alert_hours = hoursValue;
  return body;
}

export default function RadiationSettingsPage() {
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canWrite = canAdminRad(user?.role, elevated);
  const query = useRadSettings();
  const save = useUpdateRadSettings();
  const failure = queryLoadFailure(query);
  const settings = query.data?.settings ?? null;
  const doseUnit = query.data?.meta?.units.dose ?? '';
  const [texts, setTexts] = useState<RadSettingsTexts | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed once; a background refetch must not wipe what the reader is typing.
  useEffect(() => {
    if (settings && texts === null) setTexts(radSettingsTexts(settings));
  }, [settings, texts]);

  if (failure) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice
          error={queryFailureError(query)}
          kind={failure}
          subject="radiation settings"
          allowed="admins, office staff, supervisors, dispatchers, field workers and logistics"
        />
      </div>
    );
  }

  if (query.isLoading || !settings || !texts) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading radiation settings" />
      </div>
    );
  }

  const body = radSettingsBody(settings, texts, doseUnit);
  const dirty = typeof body === 'string' || Object.keys(body).length > 0;

  const onSave = () => {
    setError(null);
    if (typeof body === 'string') return setError(body);
    save.mutate(body, {
      onSuccess: (saved) => {
        if (saved) setTexts(radSettingsTexts(saved));
        toast.success('Radiation settings saved');
      },
      onError: (e) => setError(radErrorMessage(e, 'The settings could not be saved.')),
    });
  };

  const fields: Array<{ key: keyof RadSettingsTexts; label: string; hint: string; mode: 'numeric' | 'decimal' | 'text' }> = [
    { key: 'interval', label: 'Leak-test interval (months)', mode: 'numeric',
      hint: 'Sets the next-due day of a leak test recorded without one. Each test keeps the interval it was recorded under.' },
    { key: 'warnDays', label: 'Warning days', mode: 'text',
      hint: 'Up to five, comma separated. A leak test reads Due soon, and admins and office staff are notified, at each.' },
    { key: 'doseLevel', label: `Dose investigation level (${doseUnit || '—'})`, mode: 'decimal',
      hint: 'A dose record at or above this is flagged. Blank for none.' },
    { key: 'shieldHours', label: 'Out-of-shield alert (hours)', mode: 'decimal',
      hint: 'A source out of its shield longer than this is flagged. Blank for none.' },
  ];

  return (
    <div className="min-h-full" data-testid="rad-settings-page">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-4">
        {canWrite && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || save.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}

        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-white">Radioactive sources</h3>
            <p className="mt-1 text-sm text-fieldiq-text">
              Your company&rsquo;s leak-test schedule and alert thresholds for sealed sources. Check them against your
              licence conditions.
            </p>
            {!settings.stored && (
              <p className="mt-1 text-sm text-fieldiq-muted" data-testid="rad-settings-defaults">
                Nothing has been saved yet, so these are the standard defaults.
              </p>
            )}
            {!canWrite && <p className="mt-1 text-sm text-fieldiq-muted">Only an admin or office staff can change these.</p>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <label key={f.key} className="block">
                <span className="mb-1.5 block text-xs font-medium text-fieldiq-text">{f.label}</span>
                <input
                  className={inputCls}
                  inputMode={f.mode}
                  value={texts[f.key]}
                  disabled={!canWrite}
                  aria-label={f.label}
                  onChange={(e) => setTexts({ ...texts, [f.key]: e.target.value })}
                />
                <span className="mt-1 block text-xs text-fieldiq-muted">{f.hint}</span>
              </label>
            ))}
          </div>
          {error && <p role="alert" className={RAD_ALERT}>{error}</p>}
        </div>
      </div>
    </div>
  );
}
